import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import type { CohortAudience } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import EnterpriseLearnerCohortFetchDoc from 'bundles/enterprise-admin-messaging-v2/queries/EnterpriseLearnerCohortFetch.graphql';
import type {
  EnterpriseLearnerCohortFetchQuery,
  EnterpriseLearnerCohortFetchQueryVariables,
} from 'bundles/enterprise-admin-messaging-v2/queries/__generated__/EnterpriseLearnerCohortFetch';

type Params = {
  cohortAudience?: CohortAudience;
  /** Emails to check membership for (one cohort API call per email, in parallel). */
  emails: string[];
};

type Result = {
  /** Emails that ARE in the cohort (i.e., returned results when searched). */
  memberEmails: Set<string>;
  loading: boolean;
  error: boolean;
};

/**
 * Fires N parallel `fetchEnterpriseLearnerCohorts` queries (one per email in
 * `emails`) to determine which learners belong to the NLQ cohort. Returns a
 * stable `Set<string>` of emails that matched.
 *
 * Skips when `cohortAudience` is absent or `emails` is empty. Re-runs whenever
 * the email list identity changes (compared by joined string for stability).
 */
export const useCohortMembershipCheck = ({ cohortAudience, emails }: Params): Result => {
  const client = useApolloClient();
  const [memberEmails, setMemberEmails] = React.useState<Set<string>>(() => new Set());
  const [fetching, setFetching] = React.useState(false);
  const [error, setError] = React.useState(false);

  const emailsKey = emails.join(',');

  // Track which emailsKey has been resolved so we can derive loading
  // synchronously during render — no gap between step 1 completing and
  // step 2's effect setting loading to true.
  const [resolvedKey, setResolvedKey] = React.useState('');

  const shouldCheck = Boolean(cohortAudience) && emails.length > 0;
  const pending = shouldCheck && emailsKey !== resolvedKey;
  const loading = fetching || pending;

  React.useEffect(() => {
    if (!cohortAudience || emails.length === 0) {
      setMemberEmails(new Set());
      setFetching(false);
      setError(false);
      setResolvedKey(emailsKey);
      return;
    }

    let cancelled = false;
    setFetching(true);
    setError(false);

    const checkAll = async () => {
      try {
        const results = await Promise.all(
          emails.map((email) =>
            client
              .query<EnterpriseLearnerCohortFetchQuery, EnterpriseLearnerCohortFetchQueryVariables>({
                query: EnterpriseLearnerCohortFetchDoc,
                variables: {
                  cohortId: cohortAudience.cohortId,
                  orgId: cohortAudience.orgId,
                  searchString: email,
                  pageSize: 1,
                  pageToken: 0,
                },
                context: { clientName: 'gatewayGql' },
                fetchPolicy: 'no-cache',
              })
              .then((res) => {
                const learners = res.data?.EnterpriseLearnerCohort?.fetchEnterpriseLearnerCohorts?.learners ?? [];
                return learners.some((l) => l.email === email) ? email : null;
              })
              .catch(() => null)
          )
        );

        if (!cancelled) {
          const found = new Set(results.filter((e): e is string => e !== null));
          setMemberEmails(found);
          setFetching(false);
          setResolvedKey(emailsKey);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setFetching(false);
          setResolvedKey(emailsKey);
        }
      }
    };

    checkAll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, cohortAudience?.cohortId, cohortAudience?.orgId, emailsKey]);

  return { memberEmails, loading, error };
};

export default useCohortMembershipCheck;
